/**
 * Supported display locales for the LangHub UI. Single source of truth: it drives both the language
 * picker and Panther pack resolution, so the two can never disagree about which locales exist.
 *
 * The set is the locales we hold a pack for under `translations/`, plus the nine English variants,
 * which hold no pack on purpose: `en-US` is the source locale, so the root bundle is already their
 * copy. Everywhere else a missing pack reads as a bug rather than an untranslated locale.
 * `locales.test.ts` fails if this list and that directory diverge; check the family's locale list
 * before adding a tag: https://w.amazon.com/bin/view/INTech/Panther/I18nFamily/#Hretail
 */

export interface SupportedLocale {
  readonly value: string;
  readonly label: string;
}

/** Source locale for all PUFF-J resources, and the fallback for everything else. */
export const DEFAULT_LOCALE = 'en-US';

/** Sorted so the English, Spanish and French groups stay together in the picker. The optional
 * second element overrides the tag used to generate the display label only: `Intl.DisplayNames`
 * names Chinese by region ("中文（中国）") where the script name ("简体中文") is what readers expect.
 * Mirrors LUI's `regionAlias`; never affects pack resolution. */
const LOCALE_TAGS: readonly (readonly [tag: string, displayTag?: string])[] = [
  ['ar-AE'],
  ['bn-IN'],
  ['cs-CZ'],
  ['da-DK'],
  ['de-DE'],
  ['en-AE'],
  ['en-AU'],
  ['en-CA'],
  ['en-GB'],
  ['en-IE'],
  ['en-IN'],
  ['en-SG'],
  ['en-US'],
  ['en-ZA'],
  ['es-ES'],
  ['es-MX'],
  ['es-US'],
  ['fr-BE'],
  ['fr-CA'],
  ['fr-FR'],
  ['he-IL'],
  ['hi-IN'],
  ['it-IT', 'it'],
  ['ja-JP'],
  ['kn-IN'],
  ['ko-KR'],
  ['ml-IN'],
  ['mr-IN'],
  ['nb-NO'],
  ['nl-BE'],
  ['nl-NL'],
  ['pl-PL'],
  ['pt-BR'],
  ['pt-PT'],
  ['sv-SE'],
  ['ta-IN'],
  ['te-IN'],
  ['tr-TR'],
  ['zh-CN', 'zh-Hans'],
  ['zh-TW', 'zh-Hant'],
];

/**
 * The language's name in its own language. Generated because nine of these are English variants,
 * which a hand-written endonym cannot distinguish. Deliberately NOT Panther resources: a translated
 * language list would defeat the purpose of letting a reader who cannot read the current UI
 * language find their own. Native in every targeted browser, so LUI's `@formatjs` polyfill is not
 * carried over; falls back to the raw tag so the picker stays usable rather than blank.
 */
const displayName = (tag: string, displayTag: string): string => {
  try {
    return (
      new Intl.DisplayNames([displayTag], { type: 'language' }).of(
        displayTag,
      ) ?? tag
    );
  } catch {
    return tag;
  }
};

/** Computed at module scope: the picker renders on first paint, and rebuilding 40
 * `Intl.DisplayNames` instances per render would be pure waste. */
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = LOCALE_TAGS.map(
  ([tag, displayTag]) => ({
    value: tag,
    label: displayName(tag, displayTag ?? tag),
  }),
);

/** Matched on the language subtag, not the full tag: direction is a property of the script, so
 * every region of a language shares it and a future `ar-SA` needs no change here. */
const RTL_LANGUAGE_SUBTAGS: readonly string[] = ['ar', 'he', 'fa', 'ur'];

export const isSupportedLocale = (locale: string): boolean =>
  SUPPORTED_LOCALES.some((entry) => entry.value === locale);

export const isRTL = (locale: string): boolean =>
  RTL_LANGUAGE_SUBTAGS.includes(locale.split('-')[0].toLowerCase());
